import { useEffect, useRef } from 'react'
import { Chart, registerables, type ChartConfiguration } from 'chart.js'
import { palette } from '@versports/theme'
import { baseChartOptions, withAlpha } from '../../components/chartTheme'
import { formatSessionDate, toIndex100, type SessionView } from './signalsModel'

Chart.register(...registerables)

/**
 * Tendencia del índice de regulación sesión a sesión (una sesión = un punto,
 * promedio de sus ciclos). Eje de categorías con las fechas ya formateadas —
 * mismo criterio que `AdminAthleteTimeline`: evita el adapter de fechas de
 * Chart.js y las sesiones quedan equiespaciadas aunque el calendario no lo
 * esté (lo que interesa es la secuencia de entrenamientos, no el hueco).
 */
export function SignalsTrendChart({ sessions }: { sessions: SessionView[] }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const chartRef = useRef<Chart<'line'> | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return

    const labels = sessions.map((s) => formatSessionDate(s.startedAt))
    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Cardíaco',
            data: sessions.map((s) => toIndex100(s.meanRCardio)),
            borderColor: palette.primary,
            backgroundColor: withAlpha(palette.primary, 0.18),
            pointBackgroundColor: palette.primary,
            fill: true,
            tension: 0.25,
            spanGaps: true,
          },
          {
            label: 'Cardíaco + piel',
            data: sessions.map((s) => toIndex100(s.meanRCardioEda)),
            borderColor: palette.secondary,
            backgroundColor: withAlpha(palette.secondary, 0.4),
            borderDash: [5, 4],
            tension: 0.25,
            spanGaps: true,
          },
        ],
      },
      options: {
        ...baseChartOptions(),
        scales: {
          x: {
            ticks: { color: palette.textMuted },
            grid: { color: withAlpha(palette.outline, 0.4) },
          },
          y: {
            min: 0,
            max: 100,
            title: { display: true, text: 'Índice 0–100', color: palette.textMuted },
            ticks: { color: palette.textMuted },
            grid: { color: withAlpha(palette.outline, 0.4) },
          },
        },
      } as ChartConfiguration<'line'>['options'],
    }

    chartRef.current = new Chart(canvasRef.current, config)
    return () => {
      chartRef.current?.destroy()
      chartRef.current = null
    }
  }, [sessions])

  return (
    <div style={{ height: 260 }}>
      <canvas ref={canvasRef} />
    </div>
  )
}
